import React from 'react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';

const DataProtectionPolicy = () => {
    useEffect(() => {
        // Scroll to the top of the page on component load
        window.scrollTo(0, 0);
      }, []);

  return (
    <motion.div
      className="max-w-4xl mx-auto px-6 mt-20 py-10 bg-gray-50 text-zinc-700"
      initial={{ opacity: 0, y: 20 }} // Start transparent and slightly below
      animate={{ opacity: 1, y: 0 }} // Fade in and move up
      transition={{ duration: 0.8, ease: 'easeOut' }} // Smooth animation
    >
    <div className="max-w-4xl mx-auto px-6  py-10 bg-gray-50 text-zinc-700">
      <h1 className="text-3xl font-bold text-left text-zinc-900 mb-6">
        Data Protection Policy
      </h1>
      <div className="space-y-6 text-sm leading-relaxed text-justify">
        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">Introduction</h2>
          <p>
            This document summarises the policy of Get Off My Pavement Ltd in respect of Data Protection, the UK General Data Protection Regulation (UK GDPR), and the Data Protection Act 2018.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">1. Data Security - Storage</h2>
          <ul className="list-disc list-inside">
            <li>All electronic copies of personal data are stored securely using password protection and data encryption.</li>
            <li>Hardcopies of personal data, along with any electronic copies stored on physical, removable media, are kept securely in a locked box, drawer, or cabinet.</li>
            <li>All personal data stored electronically is regularly backed up.</li>
            <li>When communicating electronically (e.g., by email or over the internet), secured connections (e.g., SSL) are always used.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">2. Data Security - Disposal</h2>
          <p>
          When personal data is to be erased or disposed of for any reason, including copies that are no longer required, it will be securely deleted and destroyed to prevent unauthorised access or recovery.          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">3. Data Security - Use of Personal Data</h2>
          <ul className="list-disc list-inside">
            <li>No personal data is shared informally.</li>
            <li>Personal data is not transferred to employees, agents, contractors, or third parties without the authorisation of the company’s Data Protection Officer, Tom Johnson.</li>
            <li>Personal data must be handled carefully at all times and must not be left unattended or visible to unauthorised personnel.</li>
            <li>If personal data is being viewed on a computer screen, the device must be locked if left unattended.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">4. Data Security - IT Security</h2>
          <ul className="list-disc list-inside">
            <li>Passwords protecting personal data are changed regularly and must include a mix of uppercase and lowercase letters, numbers, and symbols.</li>
            <li>Passwords are not written down or shared between employees, agents, or contractors. If a password is forgotten, it must be reset through an approved process.</li>
            <li>All software, including operating systems, is kept up-to-date to reduce security vulnerabilities.</li>
            <li>Antivirus and firewall software are installed and updated regularly on all systems to protect against threats.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">5. Organisational Measures</h2>
          <ul className="list-disc list-inside">
            <li>All employees, agents, or contractors are fully informed of their responsibilities under the UK GDPR and this policy and are provided with a copy of the policy.</li>
            <li>Only those who need access to personal data to fulfil their duties are granted access.</li>
            <li>Employees and contractors handling personal data receive appropriate training and supervision.</li>
            <li>Employees and contractors are required to exercise care, caution, and discretion when discussing work-related matters involving personal data, both within and outside the workplace.</li>
            <li>Methods of collecting, holding, and processing personal data are regularly reviewed for compliance with UK GDPR.</li>
            <li>All personal data held by the company is periodically reviewed to ensure accuracy and relevance.</li>
            <li>Contractors or other parties working on behalf of the company must ensure that their employees handle personal data in accordance with this policy and the UK GDPR.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">6. Data Subject Rights</h2>
          <p>Individuals have the following rights under UK GDPR:</p>
          <ul className="list-disc list-inside">
            <li>The right to be informed about how their data is used.</li>
            <li>The right to access their personal data.</li>
            <li>The right to correct inaccuracies in their personal data.</li>
            <li>The right to request the deletion of their personal data.</li>
            <li>The right to restrict the processing of their personal data.</li>
            <li>The right to object to the use of their data for specific purposes.</li>
            <li>The right to data portability.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">7. Cookies and Tracking</h2>
          <ul className="list-disc list-inside">
            <li>The company uses cookies on its website to enhance user experience and for essential website functionality.</li>
            <li>Users can manage their cookie preferences through their browser settings or by contacting the company.</li>
            <li>No unnecessary tracking technologies are used without the user's consent.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">8. Data Breach Notification</h2>
          <ul className="list-disc list-inside">
            <li>Any personal data breaches must be reported immediately to the Data Protection Officer, Tom Johnson.</li>
            <li>If a breach is likely to result in a risk to the rights and freedoms of data subjects (e.g., financial loss, discrimination, reputational damage), the Information Commissioner’s Office (ICO) must be informed within 72 hours.</li>
            <li>If a breach is likely to result in a high risk to the data subjects, they must be informed directly and without undue delay.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">9. Transferring Personal Data Outside the UK</h2>
          <ul className="list-disc list-inside">
            <li>The company does not currently transfer personal data outside of the UK.</li>
            <li>If such a transfer is required in the future, it will only be made to countries with adequate data protection standards as determined by the UK government.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold mb-2 text-left">10. Policy Implementation and Review</h2>
          <ul className="list-disc list-inside">
            <li>This policy is effective as of [Insert Date] and is reviewed annually or as required to reflect changes in data protection laws or company practices.</li>
            <li>All employees, agents, and contractors must adhere to this policy.</li>
            <li>Non-compliance with this policy may result in disciplinary action or termination of contracts.</li>
          </ul>
        </section>
      </div>
      <footer className="text-sm text-center text-zinc-500 mt-10">
        <p>This Policy has been approved and authorised by: </p>
        <p>Name: Tom Johnson</p>
        <p>Position: Director</p>
        <p>Date: 25 November 2024 | Due for Review: 24 November 2025</p>
      </footer>
    </div></motion.div>
  );
};

export default DataProtectionPolicy;
